// Tossit WebView <-> Native auth bridge.
// When the web app runs inside the Tossit native app, window.TossitNative is injected.
// In a normal browser it is absent, so always feature-detect first.

import { isTossitNativeApp } from './location';

export { isTossitNativeApp };

// Platform of the current client: 'android' | 'ios' | 'web'.
export const getPlatform = () => {
    try {
        if (window.TossitNative && window.TossitNative.platform) {
            return String(window.TossitNative.platform).toLowerCase();
        }
        if (isTossitNativeApp()) {
            const ua = (navigator.userAgent || '').toLowerCase();
            if (ua.includes('android')) return 'android';
            if (/iphone|ipad|ipod/.test(ua)) return 'ios';
        }
    } catch (e) { /* ignore */ }
    return 'web';
};

// Ask the native host for the current auth token.
// Supports both a function (sync or promise) and a plain property.
// Returns the token string, or null when unavailable.
export const getNativeAuthToken = async () => {
    if (!isTossitNativeApp()) return null;
    try {
        if (typeof window.TossitNative.getAuthToken === 'function') {
            const t = await window.TossitNative.getAuthToken();
            return t || null;
        }
        if (window.TossitNative.authToken) return window.TossitNative.authToken;
    } catch (e) {
        // swallow — host may not implement it
    }
    return null;
};

// Open an external link in a webview-safe way (mirrors how share/call avoid
// window.open('_blank'), which crashes some in-app webviews). Prefers a native
// host opener if present, else navigates the SAME tab. Returns true on success.
export const openExternalLink = (url) => {
    if (!url) return false;
    if (isTossitNativeApp() && typeof window.TossitNative.openUrl === 'function') {
        try { window.TossitNative.openUrl(url); return true; } catch (e) { /* fall through */ }
    }
    try { window.location.href = url; return true; } catch (e) { return false; }
};

// Notify the native host to log out (clear its session / show native login).
// Called on explicit sign-out AND on token expiry, so the web app never has to
// show its own login screen inside the WebView.
export const nativeLogout = () => {
    if (isTossitNativeApp() && typeof window.TossitNative.logout === 'function') {
        try { window.TossitNative.logout(); } catch (e) { /* noop */ }
    }
};

// Ask the native host to style the OS status bar. style is "dark" | "light"
// (refers to the icon/text color). backgroundColor is optional (hex string).
export const setStatusBarStyle = (style, backgroundColor) => {
    if (isTossitNativeApp() && typeof window.TossitNative.setStatusBarStyle === 'function') {
        try { window.TossitNative.setStatusBarStyle(style, backgroundColor); } catch (e) { /* noop */ }
    }
};

// Whether the native app supports/allows attendance marking right now. The web
// shows the Mark-Attendance UI only when this returns true. Defaults to false
// when not in-app or the method is unavailable.
export const canMarkAttendance = async () => {
    if (!isTossitNativeApp() || !window.TossitNative || typeof window.TossitNative.canMarkAttendance !== 'function') {
        return false;
    }
    try {
        const r = await window.TossitNative.canMarkAttendance();
        return r === true || r === 'true';
    } catch (e) {
        return false;
    }
};

// Trigger the fully-native attendance flow. The native app runs presign, camera
// capture, upload AND the /cook/attendance/complete call itself; the web only
// awaits completion and then refreshes status. Supports a Promise return OR a
// window.tossitAttendanceUploaded(result) callback.
export const markAttendance = () => {
    return new Promise((resolve, reject) => {
        if (!isTossitNativeApp() || !window.TossitNative || typeof window.TossitNative.markAttendance !== 'function') {
            reject(new Error('not-in-app'));
            return;
        }
        let settled = false;
        const done = (r) => { if (!settled) { settled = true; try { delete window.tossitAttendanceUploaded; } catch (e) {} resolve(r); } };
        window.tossitAttendanceUploaded = done;
        try {
            const ret = window.TossitNative.markAttendance();
            if (ret && typeof ret.then === 'function') ret.then(done).catch(reject);
        } catch (e) {
            reject(e);
        }
    });
};

// Trigger a native UPI-app payment. The native host opens the chosen UPI app with the
// Cashfree paymentSessionId and drives the flow; the web then navigates to /payment-status
// and confirms authoritatively via /payments/verify + the webhook. Mirrors markAttendance:
// supports BOTH a directly-resolved promise AND a later window.tossitUpiPaymentResolved(result)
// callback — whichever the mobile side finds easier.
export const payViaUpi = ({ paymentSessionId, orderId, upiAppId }) => {
    return new Promise((resolve, reject) => {
        if (!isTossitNativeApp() || !window.TossitNative || typeof window.TossitNative.payViaUpi !== 'function') {
            reject(new Error('not-in-app'));
            return;
        }
        let settled = false;
        const done = (r) => { if (!settled) { settled = true; try { delete window.tossitUpiPaymentResolved; } catch (e) {} resolve(r); } };
        window.tossitUpiPaymentResolved = done;
        try {
            const ret = window.TossitNative.payViaUpi({ paymentSessionId, orderId, upiAppId });
            if (ret && typeof ret.then === 'function') ret.then(done).catch(reject);
        } catch (e) {
            reject(e);
        }
    });
};


// Arm native shift-trail background location tracking (same as the backend's
// start_tracking FCM push). The native side resolves today's shift window and posts
// the cook's live position to the trail until the shift ends. Feature-detected per the
// spec (older app builds lack it). Idempotent + self-healing by design, so it's safe to
// call on every dashboard load and on foreground — repeat calls re-arm a subscription
// that may have silently died and skip the forced backend config refresh. Never throws;
// resolves to { success } and is a no-op (success:false, error:'unavailable') off-app.
export const startLocationTracking = () => {
    return new Promise((resolve) => {
        try {
            if (window.TossitNative?.isAvailable &&
                typeof window.TossitNative.startLocationTracking === 'function') {
                const ret = window.TossitNative.startLocationTracking();
                if (ret && typeof ret.then === 'function') {
                    ret.then((r) => resolve(r || { success: true }))
                       .catch((e) => resolve({ success: false, error: String(e?.message || e) }));
                    return;
                }
                resolve(ret || { success: true });
                return;
            }
        } catch (e) {
            resolve({ success: false, error: String(e?.message || e) });
            return;
        }
        resolve({ success: false, error: 'unavailable' });
    });
};

// Read the analytics session that the NATIVE app's GA4 SDK owns, so web + native events
// stitch into ONE session inside the WebView (instead of the web minting its own, which
// would inflate session counts and split funnels). Native provides it either as a global
//   window.tossitAnalyticsSession = { session_id, session_number }
// (set/replaced by native on load and on every GA4 session refresh), or via a sync getter
//   window.TossitNative.getAnalyticsSession() -> { session_id, session_number }.
// Returns null outside the app or when native hasn't provided one yet — the web analytics
// service then falls back to its own per-tab session id.
export const getAnalyticsSession = () => {
    try {
        const g = typeof window !== 'undefined' ? window.tossitAnalyticsSession : null;
        if (g && g.session_id) return { session_id: String(g.session_id), session_number: g.session_number };
        if (isTossitNativeApp() && window.TossitNative && typeof window.TossitNative.getAnalyticsSession === 'function') {
            const s = window.TossitNative.getAnalyticsSession();
            if (s && s.session_id) return { session_id: String(s.session_id), session_number: s.session_number };
        }
    } catch (e) { /* noop */ }
    return null;
};

// Reset the in-app WebView navigation/back stack (e.g. after a completed payment so the
// hardware Back button can't return to the cart/payment screens). Prefers a native host
// method if present; otherwise falls back to a FULL reload of the target path, which
// re-initialises the SPA and clears its pushState history. Defaults to Home ('/').
export const resetNavHistory = (path = '/') => {
    if (isTossitNativeApp() && window.TossitNative) {
        try {
            if (typeof window.TossitNative.resetHistory === 'function') { window.TossitNative.resetHistory(path); return; }
            if (typeof window.TossitNative.navigateHome === 'function') { window.TossitNative.navigateHome(); return; }
        } catch (e) { /* fall through to reload */ }
    }
    try { window.location.replace(path); } catch (e) { window.location.href = path; }
};
